import { convertStatusEnumToNumericValue } from '../../shared/mission'
import {
  getWeekNumberAndYear,
  formatShortLocalDateStringToDate,
  getDateOfISOWeek,
  formatToLocalDate,
  dropTimeZoneFromDate,
} from '../../shared/libraries/utils/date'

/**
 * Converts dates into a set of week numbers that includes all the dates.
 * NOTE: Note stateless, also returns 2 weeks into the future
 * @param {Array<string>} dates
 * @returns {Array<string>}
 */
export function getWeekNumbersFromDates(dates) {
  // We store available week numbers and years in a hashmap so that
  // we don't end up with duplicates
  var localWeekNumbersHashmap = {}
  dates.forEach(dateStr => {
    // Add every week up until 2 weeks into the future
    var curDate = new Date(dropTimeZoneFromDate(dateStr))
    var cutOffDate = new Date()
    cutOffDate.setDate(cutOffDate.getDate() + 14)
    while (curDate < cutOffDate) {
      var weekInfo = getWeekNumberAndYear(curDate)
      var { weekNumber, year } = weekInfo
      localWeekNumbersHashmap['' + year + weekNumber] = weekInfo
      curDate.setDate(curDate.getDate() + 7)
    }
  })
  return Object.values(localWeekNumbersHashmap)
}
/**
 * Returns the week number unless the date is for last year, then it appends
 * the year to the returned string
 * @param {Date} date
 * @returns {string}
 */
export const getDisplayedWeekNumber = function(date) {
  const currentYear = new Date().getFullYear()
  const { weekNumber, year } = getWeekNumberAndYear(date)
  // If it's the current year then ommit the year part
  let label
  if (year == currentYear) label = `W${weekNumber}`
  else label = `W${weekNumber} (${year})`
  return label
}

export const dateSort = function(a, b) {
  a = formatShortLocalDateStringToDate(a)
  b = formatShortLocalDateStringToDate(b)

  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * @property {obj} a {year: '2020', weekNum: '27'}
 * @property {obj} b {year: '2019', weekNum: '25'}
 * @return {number} 1 or -1
 *
 * function for sorting week in a tables. use standart method array.sort
 */
export const weekSortBase = (a, b) => {
  const aYear = Number(a.year)
  const bYear = Number(b.year)
  const aWeekNum = Number(a.weekNum)
  const bWeekNum = Number(b.weekNum)

  if (aYear == bYear) {
    return aWeekNum < bWeekNum ? -1 : aWeekNum > bWeekNum ? 1 : 0
  } else {
    return aYear < bYear ? -1 : 1
  }
}

/**
 * @typedef Week
 * @property {number} weekNumber
 * @property {number} year
 *
 * Sorts week number objects
 * @param {Week} a
 * @param {Week} b
 */
export const weekSort = function(a, b) {
  a = getWeekAndYearFromWeekString(a)
  b = getWeekAndYearFromWeekString(b)

  return weekSortBase(a, b)
}

export const getWeekAndYearFromWeekString = str => {
  const match = str.match(/W(\d{1,2})(\s\((\d{4})\))?/)
  if (!match) throw new Error('Invalid week number string.')
  const weekNum = Number(match[1]),
    year = match[3] ? Number(match[3]) : new Date().getFullYear()

  return { weekNum, year }
}

export const numberInStringSort = function(a, b) {
  const itemA = parseFloat(a.label) || a.label
  const itemB = parseFloat(b.label) || b.label
  if (!isNaN(itemA) && isNaN(itemB)) return -1
  if (isNaN(itemA) && !isNaN(itemB)) return 1

  if (itemA > itemB) return 1
  if (itemA < itemB) return -1
  return 0
}

export const statusSort = function(a, b) {
  a = convertStatusLabelToNumericValue(a)
  b = convertStatusLabelToNumericValue(b)
  return a > b
}

/**
 * Sorter for user statuses
 *
 * @param {*} a fist status to compare
 * @param {*} b second status to compare
 * @returns {number} negative number for a<b, 0 if statuses are equal, positive number if a>b
 */
export const userStatusSort = function(a, b) {
  const userStatusesOrderedNumbers = {
    pending: 1,
    waiting_for_review: 2,
    active: 3,
    terminating: 4,
    blocked: 5,
    deleted: 6,
  }
  const statusA = userStatusesOrderedNumbers[a] || -1
  const statusB = userStatusesOrderedNumbers[b] || -1
  return statusA - statusB
}

export const convertStatusLabelToNumericValue = function(status) {
  if (status == 'UNDER REVIEW') status = 'UNDER_REVIEW'
  return convertStatusEnumToNumericValue(status)
}

export const dayOfWeekSort = function(a, b) {
  a = convertShortWeekDayToISOWeek(a)
  b = convertShortWeekDayToISOWeek(b)
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

export const convertShortWeekDayToISOWeek = function(str) {
  switch (str) {
    case 'Mon':
      return 1
    case 'Tue':
      return 2
    case 'Wed':
      return 3
    case 'Thu':
      return 4
    case 'Fri':
      return 5
    case 'Sat':
      return 6
    case 'Sun':
      return 7
    default:
      throw new Error("Unknown short week '" + str + "'.")
  }
}

/**
 * Sorts a time string e.g. '23:34'
 */
export const timeSort = function(a, b) {
  let [aHours, aMinutes] = a.split(':')
  let [bHours, bMinutes] = b.split(':')
  aHours = Number(aHours)
  aMinutes = Number(aMinutes)
  bHours = Number(bHours)
  bMinutes = Number(bMinutes)
  if (aHours < bHours) return -1
  if (aHours > bHours) return 1
  if (aMinutes < bMinutes) return -1
  if (aMinutes > bMinutes) return 1
  return 0
}

export const timeOfDaySort = function(a, b) {
  a = convertTimeOfDayToNumber(a)
  b = convertTimeOfDayToNumber(b)
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

export const convertTimeOfDayToNumber = str => {
  switch (str) {
    case 'MORNING':
      return 1
    case 'LUNCH':
      return 2
    case 'AFTERNOON':
      return 3
    default:
      throw new Error('Unrecognised time of day.')
  }
}

export const defaultOptionSort = function(a, b) {
  if (a.label < b.label) return -1
  if (a.label > b.label) return 1
  return 0
}
export const defaultSort = function(a, b) {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

export const defaultMissionFilters = [
  {
    label: 'Status',
    options: [
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Available', value: 'AVAILABLE' },
      { label: 'Booked', value: 'BOOKED' },
      { label: 'Checked In', value: 'CHECKED_IN' },
      { label: 'Under Review', value: 'UNDER_REVIEW' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Cancelled', value: 'CANCELLED' },
      { label: 'Paid', value: 'PAID' },
    ],
  },
  {
    label: 'Time of day',
    options: [
      { value: 'MORNING', label: 'Morning' },
      { value: 'LUNCH', label: 'Lunch' },
      { value: 'AFTERNOON', label: 'Afternoon' },
    ],
    sorted: false,
  },

  {
    label: 'Day of week',
    options: [
      { value: 'MONDAY', label: 'Monday' },
      { value: 'TUESDAY', label: 'Tuesday' },
      { value: 'WEDNESDAY', label: 'Wednesday' },
      { value: 'THURSDAY', label: 'Thursday' },
      { value: 'FRIDAY', label: 'Friday' },
      { value: 'SATURDAY', label: 'Saturday' },
      { value: 'SUNDAY', label: 'Sunday' },
    ],
    sorted: false,
  },
]

export const convertWeekAndYearToString = ({ year, weekNumber }) => {
  if (year == new Date().getFullYear()) {
    return `W${weekNumber}`
  } else {
    return `W${weekNumber} (${year})`
  }
}

export const getWeeksFilterOptions = () => {
  var dInThreeWeeks = new Date()
  dInThreeWeeks.setDate(dInThreeWeeks.getDate() + 3 * 7)
  var d1YearAgo = new Date()
  d1YearAgo.setFullYear(d1YearAgo.getFullYear() - 1)
  var weekNumbers = getWeekNumbersFromDates([
    // These dates aren't strictly accurate but they are close enough to provide
    // a list of week numbers on the page
    dInThreeWeeks.toISOString().slice(0, -1) + '+00:00',
    d1YearAgo.toISOString().slice(0, -1) + '+00:00',
  ])
  return weekNumbers
    .map(weekInfo => {
      var { weekNumber, year } = weekInfo
      // If it's the current year then ommit the year part
      var label = convertWeekAndYearToString({ year, weekNumber })
      return { label, value: weekInfo }
    })
    .sort(function({ value: a }, { value: b }) {
      if (a.year > b.year) return -1
      if (a.year < b.year) return 1
      if (a.weekNumber > b.weekNumber) return -1
      if (a.weekNumber < b.weekNumber) return 1
      return 0
    })
}

export const getOptions = (data, sortFunction = defaultOptionSort) =>
  data.map(item => ({ label: item.name, value: item.id })).sort(sortFunction)

export const SORT_OPTIONS = {
  WEEK_ASC: 'DATETIME_ASC',
  WEEK_DESC: 'DATETIME_DESC',
  DATE_ASC: 'DATETIME_ASC',
  DATE_DESC: 'DATETIME_DESC',
  LOCATION_ASC: 'LOCATION_ASC',
  LOCATION_DESC: 'LOCATION_DESC',
  BA_ASC: 'USER_ASC',
  BA_DESC: 'USER_DESC',
  CODE_ASC: 'FLYERCODE_ASC',
  CODE_DESC: 'FLYERCODE_DESC',
  STATUS_ASC: 'STATUS_ASC',
  STATUS_DESC: 'STATUS_DESC',
  RATING_ASC: 'RATING_ASC',
  RATING_DESC: 'RATING_DESC',
  TIME_ASC: 'TIMEOFDAY_ASC',
  TIME_DESC: 'TIMEOFDAY_DESC',
  TIMEOFDAY_ASC: 'TIMEOFDAY_ASC',
  TIMEOFDAY_DESC: 'TIMEOFDAY_DESC',
  CLIENT_ASC: 'CLIENTNAME_ASC',
  CLIENT_DESC: 'CLIENTNAME_DESC',
  CITY_ASC: 'CITY_ASC',
  CITY_DESC: 'CITY_DESC',
  FLYERSDISTRIBUTED_ASC: 'FLYERSDISTRIBUTED_ASC',
  FLYERSDISTRIBUTED_DESC: 'FLYERSDISTRIBUTED_DESC',
  DAYOFWEEK_ASC: 'DAYOFWEEK_ASC',
  DAYOFWEEK_DESC: 'DAYOFWEEK_DESC',
}

export const getStartEndDateFromWeekFilter = week => {
  if (!week || !week.weekNumber) return {}
  const startDateObj = getDateOfISOWeek(week.weekNumber, week.year)
  const endDateObj = new Date(startDateObj)
  endDateObj.setDate(endDateObj.getDate() + 7)
  endDateObj.setMilliseconds(endDateObj.getMilliseconds() - 1)

  return {
    startDate: formatToLocalDate(startDateObj),
    endDate: formatToLocalDate(endDateObj),
  }
}



// WEBPACK FOOTER //
// ./src/Utils/Table/index.js